import { useCallback, useEffect, useRef, useState } from "react";
import { useExtracted } from "next-intl";
import { loadTurnstile, turnstileConfig } from "@/lib/turnstile";
import type { TurnstileApi, VerificationAction } from "@/lib/turnstile";
import { Button } from "@/components/ui/button";

type Verify = (action: VerificationAction) => Promise<string | undefined>;
interface Verification {
  action: VerificationAction;
  finish: (token?: string) => void;
  reject: (error: Error) => void;
}

export function useBotProtection() {
  const [request, setRequest] = useState<Verification | null>(null);
  const pending = useRef<Verification | null>(null);

  const verify = useCallback<Verify>((action) => {
    const config = turnstileConfig();
    if (!config.required) return Promise.resolve(undefined);
    if (!config.siteKey || pending.current)
      return Promise.reject(new Error("Verification unavailable"));
    return new Promise((resolve, reject) => {
      const next: Verification = {
        action,
        reject,
        finish: (token) => {
          if (pending.current !== next) return;
          pending.current = null;
          setRequest(null);
          if (token) resolve(token);
          else reject(new Error("Verification failed or cancelled"));
        },
      };
      pending.current = next;
      setRequest(next);
    });
  }, []);

  useEffect(
    () => () => {
      pending.current?.reject(new Error("Verification cancelled"));
      pending.current = null;
    },
    [],
  );

  const cancel = useCallback(() => pending.current?.finish(), []);

  return {
    verify,
    cancel,
    challenge: request ? (
      <Challenge action={request.action} finish={request.finish} />
    ) : null,
  };
}

function Challenge({
  action,
  finish,
}: {
  action: VerificationAction;
  finish: (token?: string) => void;
}) {
  const te = useExtracted();
  const container = useRef<HTMLDivElement>(null);
  const [interactive, setInteractive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let api: TurnstileApi | undefined;
    let widget: string | undefined;
    const fail = () => {
      if (!cancelled) finish();
    };
    const timer = setTimeout(fail, 120_000);
    void loadTurnstile()
      .then((loaded) => {
        if (cancelled || !container.current) return;
        api = loaded;
        widget = api.render(container.current, {
          sitekey: turnstileConfig().siteKey,
          action,
          theme: "auto",
          size: container.current.clientWidth >= 300 ? "flexible" : "compact",
          appearance: "interaction-only",
          callback: (token) => {
            if (!cancelled) finish(token);
          },
          "before-interactive-callback": () => {
            if (!cancelled) setInteractive(true);
          },
          "error-callback": fail,
          "expired-callback": fail,
          "timeout-callback": fail,
        });
      })
      .catch(fail);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (widget !== undefined) api?.remove(widget);
    };
  }, [action, finish]);

  return (
    <div className="w-full">
      {interactive && (
        <p role="status" className="text-muted-foreground mt-3 mb-2 text-sm">
          {te("Complete this check to receive your login PIN.")}
        </p>
      )}
      <div ref={container} className="w-full" />
      {interactive && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => finish()}
        >
          {te("Cancel")}
        </Button>
      )}
    </div>
  );
}
